import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpService } from '../../../utils/apiService';

const invalidateFactoryQueries = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['factories'] });
  queryClient.invalidateQueries({ queryKey: ['factory-select'] });
};

export const useFetchFactories = ({ operatorId, page = 1, search = '', limit = 10 } = {}) => {
  return useQuery({
    queryKey: ['factories', operatorId, page, search, limit],
    queryFn: async () => {
      const response = await httpService.post('/factory/list', {
        operatorId,
        offset: page,
        limit,
        search: search || undefined,
      });
      return response.data.data;
    },
    keepPreviousData: true,
    enabled: !!operatorId,
    refetchOnWindowFocus: false,
  });
};

export const useFetchFactorySelect = ({ operatorId, search = '', kind } = {}, options = {}) => {
  return useQuery({
    queryKey: ['factory-select', operatorId, search, kind || 'ALL'],
    queryFn: async () => {
      const response = await httpService.post('/factory/get-factory-select', {
        operatorId,
        search: search || undefined,
        ...(kind ? { kind } : {}),
      });
      return response.data.data?.paginatedData || [];
    },
    enabled: !!operatorId,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useCreateFactory = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;
  return useMutation({
    mutationKey: ['create-factory'],
    mutationFn: async (data) => {
      const response = await httpService.post('/factory/create', data);
      return response.data;
    },
    onSuccess: (...args) => {
      invalidateFactoryQueries(queryClient);
      onSuccess?.(...args);
    },
    ...rest,
  });
};

export const useUpdateFactory = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;
  return useMutation({
    mutationKey: ['update-factory'],
    mutationFn: async ({ id, ...data }) => {
      const response = await httpService.post(`/factory/update/${id}`, data);
      return response.data;
    },
    onSuccess: (...args) => {
      invalidateFactoryQueries(queryClient);
      onSuccess?.(...args);
    },
    ...rest,
  });
};

export const useDeleteFactory = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;
  return useMutation({
    mutationKey: ['delete-factory'],
    mutationFn: async ({ id, operatorId }) => {
      const response = await httpService.post(`/factory/delete/${id}`, { operatorId });
      return response.data;
    },
    onSuccess: (...args) => {
      invalidateFactoryQueries(queryClient);
      onSuccess?.(...args);
    },
    ...rest,
  });
};
